<template>
  <div v-if="hasMedia" class="relative my-8">
    <Heading
      :level="2"
      :title="t('product.event.customerMedia.title')"
      h-class="text-h4"
      class="kft-heading--left mb-4"
    />
    <EventGalleryGrid
      :principal-image="principalImage"
      :featured-images="featuredImages"
      :has-vertical-video="false"
      @click:image="togglePreviewModalOpen"
    >
      <template #action-buttons>
        <div class="absolute top-1 right-1 flex gap-2 z-10">
          <Button
            color="tertiary"
            class="px-2 md:px-2 py-0.5 text-sm"
            @click.stop.prevent="togglePreviewModalOpen"
          >
            {{ t('product.event.customerMedia.seeAll') }}
          </Button>
        </div>
      </template>
    </EventGalleryGrid>

    <ClientOnly>
      <TransitionRoot as="template" :show="isPreviewModalOpen">
        <Dialog class="relative z-[100]" @close="togglePreviewModalOpen">
          <div class="fixed inset-0 flex w-screen items-center justify-center">
            <TransitionChild
              enter="duration-300 ease-out"
              enter-from="opacity-0 translate-y-full"
              enter-to="opacity-100 translate-y-0"
              leave="duration-200 ease-in"
              leave-from="opacity-100 translate-y-0"
              leave-to="opacity-0 translate-y-full"
              as="template"
            >
              <DialogPanel class="flex flex-col w-full h-full bg-white">
                <DialogTitle class="p-4 text-base">
                  <div>
                    <Button
                      color="tertiary"
                      class="px-2 border border-gray-300 !rounded-full"
                      @click.prevent="togglePreviewModalOpen"
                    >
                      <IconChevronDown class="rotate-90" />
                    </Button>
                  </div>
                </DialogTitle>
                <DialogDescription class="p-4 overflow-x-auto">
                  <EventGalleryPreview
                    :images="allGalleryItemsWithReviewInfo"
                    @click:review="handleOpenReview"
                  />
                </DialogDescription>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue'
import IconChevronDown from '~/components/icons/IconChevronDown.vue'
import type { EventDescription, GalleryItem } from '~/composables/types/event'

const props = defineProps<{
  event: EventDescription
}>()

const emit = defineEmits<{
  (e: 'open-review', reviewId: string): void
}>()

const { t } = useI18n()
const isPreviewModalOpen = ref(false)

const togglePreviewModalOpen = () => {
  isPreviewModalOpen.value = !isPreviewModalOpen.value
}

const openReviewInSidebar = inject<((reviewId: string) => void) | undefined>(
  'openReviewInSidebar',
  undefined
)

const handleOpenReview = (reviewId?: string) => {
  if (!reviewId) return
  isPreviewModalOpen.value = false
  if (openReviewInSidebar) {
    openReviewInSidebar(reviewId)
  } else {
    emit('open-review', reviewId)
  }
}

// Extract all media from reviews with dates and review info (customer, rating)
const allReviewMediaWithReviewInfo = computed(() => {
  return eventGetters.getAllReviewMediaWithReviewInfo(props.event)
})

// Transform to GalleryItem[] with optional review overlay data (customerName, rating, reviewId)
const allGalleryItemsWithReviewInfo = computed(() => {
  return allReviewMediaWithReviewInfo.value.map(
    ({ media, customer, rating, reviewId }) => ({
      ...eventGetters.transformMediaItemToGalleryItem(media),
      customerName: customer,
      rating,
      reviewId,
    })
  )
})

// Plain gallery items (for grid that doesn't need overlay)
const allGalleryItems = computed(() => {
  return allGalleryItemsWithReviewInfo.value.map(
    ({ customerName: _c, rating: _r, ...item }) => item
  )
})

// Check if there's any media to display (need at least 4 media items)
const hasMedia = computed(() => {
  return allGalleryItems.value.length >= 4
})

// Compute principalImage (first item)
const principalImage = computed(() => {
  const firstItem = allGalleryItems.value[0]
  if (!firstItem) return {} as GalleryItem
  return {
    ...firstItem,
    src: firstItem.cropped || firstItem.original,
  }
})

// Compute featuredImages (remaining items, limited to 4 for preview)
const featuredImages = computed(() => {
  return allGalleryItems.value.slice(1, 5).map((image) => ({
    ...image,
    src: image.cropped || image.original,
  }))
})
</script>
